// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { User, UserIdentifier } from '@/account/api/get-organization-users';
import { PersonSearch } from '@/components/person-search';
import {
  Control,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';

interface Props<Values extends FieldValues> extends UseControllerProps<Values> {
  /**
   * The label attached to the input element.
   */
  label?: string;

  /**
   * A react-hook-form control object used to connect the input element to the
   * form.
   */
  control: Control<Values>;
  /**
   * The placeholder text to display in the input element.
   * @default 'Search for a person'
   */
  placeholder?: string;

  /**
   * An optional callback fired when the selected person changes.
   */
  onChange?: (value: User | null) => void;
}

/**
 * A controlled person search component that integrates with react-hook-form.
 */
export function FormPersonSearch<Values extends FieldValues>(
  props: Props<Values>
) {
  const { field, fieldState } = useController({
    name: props.name,
    control: props.control,
  });

  // This component is always controlled, so an undefined value must be
  // coalesced to null.
  const value = (field.value as UserIdentifier) ?? null;

  return (
    <PersonSearch
      ref={field.ref}
      name={field.name}
      label={props.label}
      value={value}
      onChange={
        props.onChange
          ? (value) => {
              props.onChange!(value);
              field.onChange(value);
            }
          : field.onChange
      }
      error={Boolean(fieldState.error)}
      helperText={fieldState.error?.message}
      placeholder={props.placeholder}
    />
  );
}
